// =====================================================================
// src/lib/v2/firestore/openSubscribers.js
//
// V2 Firestore data layer for /businesses/{bizId}/openSubscribers.
//
// Backs the "🔔 Notify when open" button on BusinessProfile.jsx (Phase A
// Step 6 Part B). When a customer subscribes:
//   1. We write a doc here with the customer's UID as the doc ID
//   2. When the merchant flips manualOpenOverride.open → true, the
//      MerchantApp client writes a sidecar /businessOpenEvents/ doc
//   3. That fires the notifyOpenSubscribers Cloud Function, which fans
//      out a notification to every subscriber here AND deletes their
//      subscriber doc (one-shot — they have to re-subscribe if they
//      want notification on the NEXT open event)
//
// SCHEMA (enforced by firestore.rules):
//   - Doc ID: auth.currentUser.uid (the customer subscribing)
//   - subscribedAt: serverTimestamp
//   - Read: allowed for the doc's own user only (isMe(userId))
//   - Create: allowed for the doc's own user only
//   - Update: blocked (immutable — re-create if needed)
//   - Delete: allowed for the doc's own user (manual unsubscribe)
//
// Usage from BusinessProfile.jsx:
//   const subscribed = await isSubscribedToOpenAlert(businessId);
//   // ...
//   await subscribeToOpenAlert(businessId);
//   // ...
//   await unsubscribeFromOpenAlert(businessId);
//
// =====================================================================

import {
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { auth, db } from '../../firebase';

// ============================================================
// isSubscribedToOpenAlert(businessId)
// ============================================================
//
// Returns true if the current user has an active "notify when open"
// subscription on this business. Returns false if not signed in, no
// businessId, or no subscription exists.
//
// Note on the catch: if the rule denies (shouldn't happen with our
// isMe rule for the user's own doc, but defensive), treat as "not
// subscribed" — same pattern as getConnection's null-resource handling.
//
export async function isSubscribedToOpenAlert(businessId) {
  if (!businessId) return false;
  const uid = auth.currentUser?.uid;
  if (!uid) return false;
  try {
    const ref = doc(db, 'businesses', businessId, 'openSubscribers', uid);
    const snap = await getDoc(ref);
    return snap.exists();
  } catch (err) {
    if (err?.code === 'permission-denied') return false;
    throw err;
  }
}

// ============================================================
// subscribeToOpenAlert(businessId)
// ============================================================
//
// Writes the current user's subscriber doc. Idempotent — re-subscribing
// just refreshes subscribedAt (setDoc overwrites). The Cloud Function
// notifyOpenSubscribers will delete this doc after firing, so a
// re-subscribe after a notification creates a fresh doc.
//
// Throws if not signed in or businessId missing — callers should
// already be in a signed-in state when the customer taps the button.
//
export async function subscribeToOpenAlert(businessId, reason = 'closed') {
  if (!businessId) throw new Error('subscribeToOpenAlert: businessId required');
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('Not signed in — open-alert subscription requires auth');

  // `reason` records WHY the customer is waiting, so the right Cloud Function
  // wakes them:
  //   'closed'   — business is active but outside opening hours. Woken by
  //                notifyOpenSubscribers when the owner toggles open.
  //   'inactive' — business is not transactable at all (Aangan Connect lapsed
  //                or pending-verification trial expired). Woken by
  //                onActivationEvent when the owner reactivates.
  //
  // Both cohorts share this one collection, so without this field a
  // reactivation would ping people who only wanted the open ping (and delete
  // their subscription before they got it), and vice-versa. Legacy docs
  // written before this field are treated as 'closed' by onActivationEvent's
  // filter, which preserves today's behaviour exactly.
  const ref = doc(db, 'businesses', businessId, 'openSubscribers', uid);
  await setDoc(ref, {
    subscribedAt: serverTimestamp(),
    reason: reason === 'inactive' ? 'inactive' : 'closed',
  });
}

// ============================================================
// unsubscribeFromOpenAlert(businessId)
// ============================================================
//
// Deletes the current user's subscriber doc. Idempotent — deleting a
// non-existent doc throws permission-denied (the rule can't evaluate
// against null resource), which we treat as "already unsubscribed".
//
// Throws if not signed in or businessId missing.
//
export async function unsubscribeFromOpenAlert(businessId) {
  if (!businessId) throw new Error('unsubscribeFromOpenAlert: businessId required');
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('Not signed in — open-alert unsubscribe requires auth');
  const ref = doc(db, 'businesses', businessId, 'openSubscribers', uid);
  try {
    await deleteDoc(ref);
  } catch (err) {
    // Already gone (function may have processed and deleted, or another
    // tab unsubscribed first). Caller's optimistic UI is already in the
    // right state.
    if (err?.code === 'permission-denied' || err?.code === 'not-found') return;
    throw err;
  }
}
